import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuáles son las alternativas a los antidepresivos?",
    answer:
      "Las alternativas incluyen TMS, SAINT® TMS acelerada, SPRAVATO® (esketamina), psicoterapia y enfoques de estilo de vida. La TMS y SAINT® usan estimulación magnética cerebral sin introducir ningún producto químico. SPRAVATO® es un aerosol nasal en consulta. La psicoterapia tiene evidencia para la depresión. La mejor opción depende de sus circunstancias.",
  },
  {
    question: "¿Es la TMS una alternativa a los antidepresivos?",
    answer:
      "Sí. La TMS está autorizada por la FDA para adultos cuya depresión no ha mejorado con antidepresivos. Usa pulsos magnéticos, no introduce ningún producto químico y no causa efectos secundarios sistémicos. La TMS puede ser una alternativa cuando los antidepresivos no han funcionado o no se desean.",
  },
  {
    question: "¿Puede SAINT sustituir a los antidepresivos?",
    answer:
      "SAINT® puede ser una opción cuando los antidepresivos no han funcionado, pero no necesariamente sustituye a la medicación. Muchos pacientes continúan con medicación durante y después de SAINT®. Si continuar, ajustar o suspender la medicación es una decisión clínica. Nunca suspenda los antidepresivos de forma brusca sin supervisión.",
  },
  {
    question: "¿Es la psicoterapia una alternativa a los antidepresivos?",
    answer:
      "Para la depresión de leve a moderada, la psicoterapia puede ser tan eficaz como la medicación. Para la depresión de moderada a grave, combinar psicoterapia con medicación u otros tratamientos suele ser más eficaz. La psicoterapia es una opción sin medicación con evidencia que puede usarse sola o junto con otros tratamientos.",
  },
  {
    question: "¿Existen alternativas naturales a los antidepresivos?",
    answer:
      "El ejercicio, el sueño regular, la luz solar, la reducción del alcohol y la conexión social tienen evidencia para síntomas leves. El hipérico tiene evidencia de ensayos para la depresión de leve a moderada pero interactúa con muchos medicamentos. Ningún suplemento está aprobado por la FDA para la depresión. Comente cualquier suplemento con su médico. Para la depresión de moderada a grave se recomienda tratamiento profesional.",
  },
];

export const esAlternativeAntidepressants = makeEsArticle({
  slug: "alternative-to-antidepressants",
  title: "Alternativas a los antidepresivos | Neuro Wellness",
  description:
    "Alternativas a los antidepresivos: TMS, SAINT®, SPRAVATO®, psicoterapia y enfoques de estilo de vida. Conozca las opciones sin medicación para el tratamiento de la depresión.",
  h1: "¿Cuáles son las alternativas a los antidepresivos?",
  breadcrumbLabel: "Alternativas a los antidepresivos",
  shortAnswer:
    "Las alternativas a la medicación antidepresiva incluyen TMS, SAINT® TMS acelerada, SPRAVATO® (esketamina), psicoterapia y enfoques de estilo de vida. La TMS y SAINT® usan estimulación magnética cerebral y no introducen ningún producto químico en el cuerpo. SPRAVATO® es un aerosol nasal en consulta que actúa por un mecanismo distinto al de los antidepresivos orales. La psicoterapia (TCC, activación conductual, terapia interpersonal) es una opción sin medicación con evidencia. Para la depresión grave o resistente al tratamiento, estas alternativas pueden ser apropiadas. La mejor opción depende de sus circunstancias clínicas y debe comentarse con un psiquiatra.",
  keyTakeaways: [
    "La TMS y SAINT® son alternativas de estimulación cerebral sin medicación.",
    "SPRAVATO® actúa por un mecanismo distinto al de los antidepresivos orales.",
    "La psicoterapia es una opción sin medicación con evidencia.",
    "Estas alternativas están autorizadas o aprobadas por la FDA para la depresión.",
    "La mejor opción depende de las circunstancias clínicas.",
  ],
  sections: [
    {
      heading: "TMS (estimulación magnética transcraneal)",
      paragraphs: [
        "La TMS está autorizada por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. Usa pulsos magnéticos para estimular la corteza prefrontal dorsolateral izquierda — una región cerebral implicada en la regulación del estado de ánimo. La TMS no introduce ningún producto químico en el cuerpo y no causa efectos secundarios sistémicos como aumento de peso o disfunción sexual. Un ciclo estándar es de unas 36 sesiones durante seis semanas. La TMS está ampliamente cubierta por los seguros para la depresión resistente al tratamiento.",
      ],
    },
    {
      heading: "SAINT® TMS acelerada",
      paragraphs: [
        "SAINT® es un protocolo de TMS acelerada — 50 sesiones en cinco días con orientación por fMRI. Al igual que la TMS, es no invasiva y no introduce ningún producto químico. SAINT® usa un objetivo personalizado elegido a partir de la propia resonancia cerebral de cada paciente. En un ensayo controlado con simulación, el 79 % del grupo activo alcanzó la remisión. SAINT® puede ser apropiada para la depresión resistente al tratamiento cuando se desea un calendario más rápido.",
      ],
    },
    {
      heading: "SPRAVATO® (esketamina)",
      paragraphs: [
        "SPRAVATO® es un aerosol nasal autorizado por la FDA para la depresión resistente al tratamiento. Actúa mediante la modulación del receptor NMDA — un mecanismo distinto al de los antidepresivos estándar (que se dirigen a la serotonina, la noradrenalina o la dopamina). SPRAVATO® se administra en consulta bajo supervisión y requiere que alguien le lleve a casa. Puede ser una alternativa para pacientes que no han respondido a los antidepresivos orales.",
      ],
    },
    {
      heading: "Psicoterapia",
      paragraphs: [
        "La psicoterapia es un tratamiento sin medicación con evidencia para la depresión. Los tipos incluyen terapia cognitivo-conductual (TCC), activación conductual, terapia interpersonal y enfoques basados en el mindfulness. Para la depresión de leve a moderada, la psicoterapia puede ser tan eficaz como la medicación. Para la depresión de moderada a grave, combinar psicoterapia con medicación u otros tratamientos suele ser más eficaz. La psicoterapia forma parte con frecuencia de un plan de tratamiento con TMS o SAINT®.",
      ],
    },
    {
      heading: "Enfoques de estilo de vida",
      paragraphs: [
        "Los enfoques de estilo de vida — ejercicio regular, sueño constante, exposición a la luz solar, reducción del alcohol y conexión social — tienen evidencia para síntomas depresivos leves. Son adiciones útiles pero rara vez suficientes como tratamientos únicos para la depresión de moderada a grave. Funcionan mejor como parte de un plan de tratamiento integral que puede incluir psicoterapia, medicación o tratamientos de estimulación cerebral.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of TMS in the acute treatment of major depression. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17123640/",
    },
    {
      citation:
        "Cuijpers P, et al. Psychotherapy for depression: a meta-analysis. World Psychiatry. 2023;22(1):105-115.",
      url: "https://pubmed.ncbi.nlm.nih.gov/36640402/",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
